import React, { useState } from 'react';
import { Loader2, Music2, Link2, PlugZap, Save, Unplug } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const REDIRECT = (process.env.REACT_APP_BACKEND_URL || '') + '/api/admin/distribution/tiktok/callback';

function Step({ n, children }) {
  return (
    <li className="flex gap-2 text-sm text-gray-600">
      <span className="w-5 h-5 rounded-full bg-black/10 text-black text-xs font-bold flex items-center justify-center shrink-0">{n}</span>
      <span>{children}</span>
    </li>
  );
}

/** Configuración y conexión de TikTok (sandbox). Guarda Client Key/Secret cifrados. */
export default function TikTokSetup({ settings, onReload }) {
  const { toast } = useToast();
  const tt = settings?.tiktok || {};
  const [form, setForm] = useState({ client_key: '', client_secret: '' });
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.saveDistributionSettings({ tiktok: { client_key: form.client_key, client_secret: form.client_secret } });
      setForm({ client_key: '', client_secret: '' });
      toast({ title: 'Credenciales TikTok guardadas (cifradas)' });
      onReload?.();
    } catch (e) {
      toast({ title: 'Error al guardar', description: String(e.message || e), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const { url } = await api.tiktokAuthorizeUrl();
      window.open(url, 'tt-oauth', 'width=600,height=760');
      toast({ title: 'Autoriza en la ventana de TikTok' });
    } catch (e) {
      toast({ title: 'No se pudo iniciar OAuth', description: String(e.message || e), variant: 'destructive' });
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('¿Desconectar TikTok? Se cierra la sesión de esta cuenta (Client Key/Secret se conservan). No afecta a las demás redes.')) return;
    setDisconnecting(true);
    try {
      await api.tiktokDisconnect();
      toast({ title: 'TikTok desconectado', description: 'Puedes reconectar con otra cuenta.' });
      onReload?.();
    } catch (e) {
      toast({ title: 'Error al desconectar', description: String(e.message || e), variant: 'destructive' });
    } finally { setDisconnecting(false); }
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100" data-testid="tiktok-setup">
      <div className="flex items-center gap-2 mb-3">
        <Music2 className="w-5 h-5 text-black" />
        <h3 className="font-bold text-habita-dark">TikTok — Content Posting API</h3>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-bold">Sandbox</span>
        {tt.connected && (
          <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold" data-testid="tiktok-connected-badge">
            {tt.display_name ? `Conectado como: @${tt.display_name}` : 'Conectado'}
          </span>
        )}
      </div>
      {tt.needs_reconnect && (
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="tiktok-reconnect-warning">
          ⚠️ La sesión de TikTok expiró y no se pudo renovar automáticamente. Vuelve a conectar la cuenta de Habita para seguir publicando.
        </div>
      )}
      <ol className="space-y-1.5 mb-4">
        <Step n={1}>Crea una app en <strong>TikTok for Developers</strong> y activa <strong>Login Kit</strong> + <strong>Content Posting API</strong>.</Step>
        <Step n={2}>Registra este <strong>Redirect URI</strong>: <code className="bg-gray-100 px-1 rounded break-all">{REDIRECT}</code></Step>
        <Step n={3}>Copia el <strong>Client Key</strong> y <strong>Client Secret</strong>, pégalos abajo y guarda.</Step>
        <Step n={4}>Pulsa <strong>Conectar TikTok</strong> y autoriza. (Direct Post público requiere auditoría de la app.)</Step>
      </ol>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <input data-testid="tiktok-client-key" placeholder={tt.client_key_set ? '•••• (guardado)' : 'Client Key'}
          value={form.client_key} onChange={(e) => setForm((f) => ({ ...f, client_key: e.target.value }))}
          className="px-3 h-10 rounded-xl border border-gray-200 outline-none focus:border-habita text-sm" />
        <input data-testid="tiktok-client-secret" type="password" placeholder={tt.client_secret_set ? '•••• (guardado)' : 'Client Secret'}
          value={form.client_secret} onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))}
          className="px-3 h-10 rounded-xl border border-gray-200 outline-none focus:border-habita text-sm" />
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={saving} data-testid="tiktok-save-btn"
          className="px-4 h-9 rounded-xl border border-gray-200 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar credenciales
        </button>
        <button onClick={connect} disabled={connecting || !tt.oauth_ready} data-testid="tiktok-connect-btn"
          title={tt.oauth_ready ? '' : 'Guarda primero Client Key y Secret'}
          className="px-4 h-9 rounded-xl bg-black text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
          {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} {(tt.connected || tt.needs_reconnect) ? 'Reconectar TikTok' : 'Conectar TikTok'}
        </button>
        {tt.connected && (
          <button onClick={disconnect} disabled={disconnecting} data-testid="tiktok-disconnect-btn"
            className="px-4 h-9 rounded-xl border border-rose-200 text-rose-600 text-sm font-semibold flex items-center gap-2 hover:bg-rose-50 disabled:opacity-50">
            {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unplug className="w-4 h-4" />} Desconectar TikTok
          </button>
        )}
      </div>
      {tt.connected && tt.display_name && (
        <p className="text-xs text-gray-600 mt-2" data-testid="tiktok-connected-as">Conectado como: <strong>@{tt.display_name}</strong></p>
      )}
      {tt.open_id && <p className="text-xs text-gray-500 mt-2">open_id: <code>{tt.open_id.slice(0, 12)}…</code> · scope: {tt.scope}</p>}
      <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-1"><PlugZap className="w-3 h-3" /> TikTok usa las imágenes originales (sin marca de agua) servidas por el media-proxy de Habita.</p>
    </div>
  );
}
